import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

const slides = [
  {
    eyebrow: "Programa de Inglés · Politécnico PIO",
    title: "Abre las puertas a nuevas oportunidades con el inglés",
    text: "Aprende inglés en el PIO con una oferta académica dirigida a la empleabilidad: desarrolla las habilidades que te ayudarán a crecer personal y profesionalmente.",
    image: "/imagenes/slider-01.webp",
    ctas: [
      { href: "#contacto", label: "Quiero estudiar inglés", primary: true, testid: "hero-slide-1-cta-primary" },
      { href: "#niveles", label: "Ver niveles", primary: false, testid: "hero-slide-1-cta-secondary" },
    ],
  },
  {
    eyebrow: "Metodología Práctica",
    title: "Aprende hablando desde la primera clase",
    text: "Enfócate en la comunicación real con docentes expertos y recursos digitales interactivos.",
    image: "/imagenes/slider-02.webp",
    ctas: [
      { href: "#metodologia", label: "Conoce la metodología", primary: true, testid: "hero-slide-2-cta-primary" },
      { href: "#niveles", label: "Ver niveles", primary: false, testid: "hero-slide-2-cta-secondary" },
    ],
  },
  {
    eyebrow: "Clases Presenciales",
    title: "Estudia a tu propio ritmo y en tu horario",
    text: "Aprende en nuestra sede con clases presenciales, práctica en grupo y acompañamiento directo de los docentes.",
    image: "/imagenes/slider-03.webp",
    ctas: [
      { href: "#modalidades", label: "Conoce la modalidad", primary: true, testid: "hero-slide-3-cta-primary" },
      { href: "#contacto", label: "Inscríbete ahora", primary: false, testid: "hero-slide-3-cta-secondary" },
    ],
  },
  {
    eyebrow: "¡Inscripciones Abiertas!",
    title: "Aprende inglés con el Politécnico PIO",
    text: "Aprende inglés de forma presencial con horarios flexibles, atención personalizada y precios especiales.",
    image: "/imagenes/slider-04.webp",
    ctas: [
      { href: "#contacto", label: "Inscríbete ahora", primary: true, testid: "hero-slide-4-cta-primary" },
      { href: "#metodologia", label: "Ver metodología", primary: false, testid: "hero-slide-4-cta-secondary" },
    ],
  },
];

const AUTOPLAY_MS = 6000;

export default function HeroSlider() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((i) => setIndex((i + slides.length) % slides.length), []);

  useEffect(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (paused || reduced) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % slides.length), AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [paused]);

  const slide = slides[index];

  return (
    <section
      id="inicio"
      data-testid="hero-slider"
      className="relative overflow-hidden bg-slate-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative min-h-[560px] lg:min-h-[660px] flex items-center">
        <AnimatePresence mode="sync">
          <motion.div
            key={index}
            className="absolute inset-0"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <img
              src={slide.image}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              data-testid={`hero-slide-${index + 1}-image`}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/55 to-slate-950/20" />
          </motion.div>
        </AnimatePresence>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-24">
          <AnimatePresence mode="wait">
            <motion.div
              key={`content-${index}`}
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.55, ease: "easeOut" }}
              className="max-w-2xl"
              data-testid={`hero-slide-${index + 1}`}
            >
              <span className="inline-flex items-center rounded-full border border-white/25 bg-white/10 backdrop-blur-sm px-4 py-1.5 text-xs font-semibold tracking-wide uppercase text-white/90">
                {slide.eyebrow}
              </span>
              <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.08]">
                {slide.title}
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-200 leading-relaxed max-w-xl">
                {slide.text}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                {slide.ctas.map((cta) => (
                  <a
                    key={cta.testid}
                    href={cta.href}
                    data-testid={cta.testid}
                    className={
                      cta.primary
                        ? "inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
                        : "inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                    }
                  >
                    {cta.label}
                    {cta.primary && <ArrowRight className="w-4 h-4" />}
                  </a>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <button
          type="button"
          aria-label="Diapositiva anterior"
          data-testid="hero-prev-button"
          onClick={() => go(index - 1)}
          className="hidden lg:flex absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full border border-white/30 bg-white/10 backdrop-blur-sm text-white items-center justify-center hover:bg-white/25 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          type="button"
          aria-label="Diapositiva siguiente"
          data-testid="hero-next-button"
          onClick={() => go(index + 1)}
          className="hidden lg:flex absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full border border-white/30 bg-white/10 backdrop-blur-sm text-white items-center justify-center hover:bg-white/25 transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        <div className="absolute bottom-6 inset-x-0 z-20 flex items-center justify-center gap-6" data-testid="hero-controls">
          <button
            type="button"
            aria-label="Diapositiva anterior"
            data-testid="hero-prev-button-mobile"
            onClick={() => go(index - 1)}
            className="lg:hidden w-10 h-10 rounded-full border border-white/30 bg-white/10 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/25 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex gap-2.5" data-testid="hero-indicators">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Ir a la diapositiva ${i + 1}`}
                data-testid={`hero-indicator-${i + 1}`}
                onClick={() => go(i)}
                className={`h-2 rounded-full transition-all duration-300 ${i === index ? "w-8 bg-white" : "w-2 bg-white/40 hover:bg-white/70"
                  }`}
              />
            ))}
          </div>
          <button
            type="button"
            aria-label="Diapositiva siguiente"
            data-testid="hero-next-button-mobile"
            onClick={() => go(index + 1)}
            className="lg:hidden w-10 h-10 rounded-full border border-white/30 bg-white/10 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/25 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
}
